import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";

import Sidebar from "../Components/Sidebars/main";
import NavBar from "../Components/Navbars/main";
import ActionBarComponent from "../Components/common/ActionBarComponent";
import PhotoIcon from "../../public/BP_resources/images/icon/copilot-logo-icon.svg";
import Footer from "../Components/common/footer";
import CoPilotTabs from "../Components/new-co-pilot-page.js/CoPilot-Tabs";
import SearchTables from "../Components/new-co-pilot-page.js/table";
import DocTable from "../Components/new-co-pilot-page.js/doc-table";
import { api_without_cancellation } from "../api/api";
import RequestCoPilotModal from "../Components/new-co-pilot-page.js/request-copilot-modal";
import { formatDateForPanelDisplay, getLoggedInFirmId } from "../Utils/helper";

const BANNER_IMG_WIDTH = 250;
const BANNER_IMG_OVERLAP = 60;
const BANNER_IMG_ADVANCE = BANNER_IMG_WIDTH - BANNER_IMG_OVERLAP; // horizontal space each image actually adds
const FADE_COUNT = 5; // outermost N images per side fade out

const BANNER_IMG_STYLE = {
  objectFit: "cover",
  height: "300px",
  width: `${BANNER_IMG_WIDTH}px`,
  minWidth: `${BANNER_IMG_WIDTH}px`,
  maxWidth: `${BANNER_IMG_WIDTH}px`,
  flexShrink: 0,
  clipPath: "polygon(24% 0%, 100% 0%, 76% 100%, 0% 100%)",
  display: "block",
};

function NewCoPilotPage() {
  const [sideBarCollapsed, setSideBarCollapsed] = useState(null);
  const [activeTab, setActiveTab] = useState("directory");
  const [search, setSearch] = useState("");
  const [firms, setFirms] = useState([]);
  const [bannerImages, setBannerImages] = useState([]);
  const [openFirms, setOpenFirms] = useState([]); // firms opened as tabs, full API objects
  const [directoryLoading, setDirectoryLoading] = useState(false);

  const bannerContainerRef = useRef(null);
  const [bannerWidth, setBannerWidth] = useState(0);

  useEffect(() => {
    const preferences = localStorage.getItem("userPreferences");
    try {
      const preferencesObj = preferences ? JSON.parse(preferences) : null;
      setSideBarCollapsed(preferencesObj?.SidebarView);
    } catch {
      setSideBarCollapsed(null);
    }
  }, []);

  // Banner: fetch once
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await api_without_cancellation.get("/api/copilot-page/banner/");
        setBannerImages(response.data?.images || []);
      } catch (err) {
        console.error("Error fetching co-pilot banner:", err);
      }
    };
    fetchBanner();
  }, []);

  // Track banner container width so the strip always overfills it
  useEffect(() => {
    const node = bannerContainerRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setBannerWidth(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [bannerImages.length, activeTab]);

  // Tile source images cyclically until the strip overflows the container on both sides.
  // Odd count keeps one image dead-center.
  const displayImages = useMemo(() => {
    if (!bannerImages.length || !bannerWidth) return [];
    let count = Math.ceil(bannerWidth / BANNER_IMG_ADVANCE) + 2; // +2 so both edges are past the viewport
    if (count % 2 === 0) count += 1;
    return Array.from({ length: count }, (_, i) => ({
      ...bannerImages[i % bannerImages.length],
      _tileIndex: i,
    }));
  }, [bannerImages, bannerWidth]);

  // Directory: refetch on search, debounced. Empty input = "All".
  useEffect(() => {
    const timer = setTimeout(async () => {
      setDirectoryLoading(true);
      try {
        const response = await api_without_cancellation.get("/api/copilot-page/directory/", {
          params: { search: search.trim() || "All" },
        });
        setFirms(response?.data?.firms ?? []);
      } catch (err) {
        console.error("Error fetching co-pilot directory:", err);
        setFirms([]);
      } finally {
        setDirectoryLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  const firmId = getLoggedInFirmId();

  const fetchDirectory = async () => {
    setDirectoryLoading(true);
    try {
      const response = await api_without_cancellation.get("/api/copilot-page/directory/", {
        params: { search: search.trim() || "All" },
      });
      setFirms(response?.data?.firms ?? []);
      const firm = response?.data?.firms.find(
        (f) => Number(f.firm_id) === Number(activeFirm?.firm_id)
      );
      if (!firm) return;
      setOpenFirms((prev) =>
        prev.some((f) => f.firm_id === firm.firm_id)
          ? prev.map((f) => (f.firm_id === firm.firm_id ? firm : f))
          : [...prev, firm]
      );
    } catch (err) {
      console.error("Error fetching co-pilot directory:", err);
      setFirms([]);
    } finally {
      setDirectoryLoading(false);
    }
  };
  // Open a firm tab (or focus it if already open) when a directory row is clicked
  const handleFirmClick = useCallback(
    (rowOrId) => {
      const firmId = typeof rowOrId === "object" ? rowOrId.firm_id : rowOrId;
      const firm = firms.find((f) => f.firm_id === firmId);
      if (!firm) return;
      setOpenFirms((prev) =>
        prev.some((f) => f.firm_id === firm.firm_id) ? prev : [...prev, firm]
      );
      setActiveTab(`firm-${firm.firm_id}`);
    },
    [firms]
  );

  const activeFirm = useMemo(
    () => openFirms.find((f) => `firm-${f.firm_id}` === activeTab) ?? null,
    [openFirms, activeTab]
  );
  // Full opacity in the core; the outermost FADE_COUNT images per side fade linearly to 0
  const bannerOpacity = useCallback(
    (index) => {
      const total = displayImages.length;
      const center = (total - 1) / 2;
      const distance = Math.abs(index - center);
      const fadeStart = center - FADE_COUNT; // last fully-opaque position
      if (distance <= fadeStart) return 1;
      return Math.max(0.1, 1 - (distance - fadeStart) / FADE_COUNT);
    },
    [displayImages.length]
  );

  const [showReqModal, setShowRequestModal] = useState(false);
  const relationship = activeFirm?.copilot_relationship;
  const request = relationship?.request;
  return (
    <>
      <div className={`page-wrapper ${sideBarCollapsed ? "force-collapsed" : ""}`}>
        <Sidebar />
        <div className="page-container overflow-hidden">
          <NavBar flaggedPageName={""} />
          <ActionBarComponent src={PhotoIcon} page_name="Co Pilot" />
          <div className="TreatmentPage-main-container invisible-scrollbar">
            <CoPilotTabs activeTab={activeTab} firmTabs={openFirms} onSelect={setActiveTab} />

            {activeTab === "directory" && (
              <>
                <div className="d-flex w-100 align-items-center justify-content-center">
                  <h2>Welcome to the CoPilot Directory. Find Your CoPilot Here</h2>
                </div>

                {bannerImages.length > 0 && (
                  <div
                    ref={bannerContainerRef}
                    style={{ width: "100%", overflow: "hidden", height: "300px" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ width: "max-content", margin: "0 auto" }}
                    >
                      {displayImages.map((img, index) => (
                        <span
                          key={`tile-${img._tileIndex}`}
                          style={{
                            position: "relative",
                            display: "inline-block",
                            flexShrink: 0,
                            opacity: bannerOpacity(index),
                            ...(index > 0 && {
                              marginLeft: `-${BANNER_IMG_OVERLAP}px`,
                            }),
                          }}
                        >
                          <img src={img.image_url} alt={img.firm_name} style={BANNER_IMG_STYLE} />
                          {img.logo && (
                            <img
                              src={img.logo}
                              alt={`${img.firm_name} logo`}
                              style={{
                                position: "absolute",
                                bottom: "0px",
                                left: "47%",
                                transform: "translateX(-50%)",
                                width: `${Math.round(BANNER_IMG_WIDTH * 0.66)}px`,
                                objectFit: "contain",
                                pointerEvents: "none",
                              }}
                            />
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="m-r-5 m-t-5 m-b-5">
                  <input
                    type="text"
                    className="form-control p-0 p-l-5"
                    placeholder="Enter Name to Search for co pilot firms"
                    style={{ height: "25px" }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <SearchTables
                  mockFirms={firms}
                  showHeader={true}
                  loading={directoryLoading}
                  onFirmClick={handleFirmClick}
                  fill
                />
              </>
            )}

            {activeFirm && (
              <div style={{ height: "calc(100% - 48px)" }}>
                <SearchTables mockFirms={[activeFirm]} showNumber={false} />
                {/* Req Button */}
                {Number(activeFirm?.firm_id) !== Number(firmId) && (
                  <>
                    <div className="d-flex align-items-center justify-content-end w-100 p-t-5 p-r-5">
                      {request?.status === "PENDING" ? (
                        <Button
                          variant="warning"
                          style={{
                            color: "white",
                            fontWeight: "600",
                          }}
                          disabled
                          className="height-25 d-flex align-items-center justify-content-center"
                        >
                          Pending Request
                        </Button>
                      ) : request?.status === "ACCEPTED" && request.direction === "outgoing" ? (
                        <Button
                          variant="success"
                          style={{
                            color: "white",
                            fontWeight: "600",
                          }}
                          disabled
                          className="height-25 d-flex align-items-center justify-content-center"
                        >
                          <>
                            Request accepted at {formatDateForPanelDisplay(request?.accepted_at)}{" "}
                            {request?.direction === "incoming"
                              ? "You can't send a co-pilot request because you are already serving as their co-pilot."
                              : ""}
                          </>
                        </Button>
                      ) : request?.status === "ACCEPTED" && request.direction === "incoming" ? (
                        <Button
                          variant="success"
                          style={{
                            color: "white",
                            fontWeight: "600",
                          }}
                          disabled
                          className="height-25 d-flex align-items-center justify-content-center"
                        >
                          <>
                            Serving from {formatDateForPanelDisplay(request?.accepted_at)}{" "}
                            {request?.direction === "incoming"
                              ? "You can't send a co-pilot request because you are already serving as their co-pilot."
                              : ""}
                          </>
                        </Button>
                      ) : relationship?.status === "none" && !relationship?.is_linked ? (
                        <Button
                          onClick={() => setShowRequestModal(true)}
                          className="height-25 d-flex align-items-center justify-content-center"
                        >
                          Request Co-Pilot
                        </Button>
                      ) : null}
                    </div>
                  </>
                )}
                {/* Description */}
                {[activeFirm.paragraph_1, activeFirm.paragraph_2, activeFirm.paragraph_3]
                  .filter((p) => p?.trim())
                  .map((paragraph, index) => (
                    <div key={index} className="text-muted">
                      <p>{paragraph}</p>
                    </div>
                  ))}

                {/* Images */}
                <div className="d-flex justify-content-center" style={{ gap: "5px" }}>
                  {[activeFirm.image_1, activeFirm.image_2, activeFirm.image_3]
                    .filter(Boolean)
                    .map((src) => (
                      <div key={src}>
                        <img
                          src={src}
                          alt={activeFirm.firm_name}
                          style={{ objectFit: "contain", height: "500px", width: "auto" }}
                        />
                      </div>
                    ))}
                </div>

                {activeFirm.case_types?.length > 0 && (
                  <DocTable activeFirm={activeFirm} caseTypes={activeFirm.case_types} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />

      {showReqModal && (
        <>
          <RequestCoPilotModal
            show={showReqModal}
            handleClose={() => setShowRequestModal(false)}
            firm={activeFirm}
            refetch={fetchDirectory}
          />
        </>
      )}
    </>
  );
}

export default NewCoPilotPage;
